import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Spinner } from "components/ui/spinner";
import { useIdentityContext } from "contexts/identity-context";
import { verifyWaitlistSubmitted } from "data/local-storage-keys";
import { Form, Formik } from "formik";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { getLocalStorage, setLocalStorage } from "utils/local-storage";
import { sendVerifyWaitlist } from "utils/pardot";
import { array, object, string } from "yup";

const VERIFY_MODULES = [
  "Address Verify",
  "Email Verify",
  "Phone Verify",
  "MailPoint Verify",
] as const;

interface WaitlistValues {
  verifyModules: string[];
}

const validationSchema = object({
  verifyModules: array().of(string()).min(1),
});

/**
 * The single CTA on the Geoscape Verify card while it is coming soon. Opens a
 * dialog to register interest in specific modules, posts the logged-in user's
 * details to Pardot, and remembers the submission so the button reflects it on
 * return visits.
 */
export const VerifyWaitlistButton = () => {
  const { user } = useIdentityContext();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(() =>
    getLocalStorage(verifyWaitlistSubmitted, false),
  );

  const waitlistMutation = useMutation({
    mutationFn: (values: WaitlistValues) =>
      sendVerifyWaitlist({
        email: user?.email ?? "",
        dodUserId: user?.cognitoSub,
        organisationId: user?.activeOrg?.organisationId,
        verifyModules: values.verifyModules,
      }),
    onSuccess: () => {
      setLocalStorage(verifyWaitlistSubmitted, true);
      setSubmitted(true);
    },
  });

  if (submitted && !open) {
    return (
      <Button
        type="button"
        variant="outline"
        disabled
        data-testid="verify-waitlist-joined"
      >
        You're on the list
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) waitlistMutation.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" data-testid="verify-waitlist-open">
          Be first to know
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Geoscape Verify is coming soon</DialogTitle>
          <DialogDescription>
            Select the verification modules you're interested in and we'll let
            you know when they're available.
          </DialogDescription>
        </DialogHeader>

        {waitlistMutation.isSuccess ? (
          <>
            <Alert data-testid="verify-waitlist-success">
              <CheckCircle2 />
              <AlertTitle>You're on the list</AlertTitle>
              <AlertDescription>
                We'll email you as each module becomes available.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" onClick={() => setOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Formik<WaitlistValues>
            initialValues={{ verifyModules: [] }}
            validationSchema={validationSchema}
            onSubmit={(values) => waitlistMutation.mutate(values)}
          >
            {({ values: { verifyModules }, isValid, dirty, setFieldValue }) => (
              <Form className="space-y-4">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {VERIFY_MODULES.map((module) => (
                    <div key={module} className="flex items-center gap-2">
                      <Checkbox
                        id={`verify-module-${module}`}
                        name="verifyModules"
                        value={module}
                        checked={verifyModules.includes(module)}
                        disabled={waitlistMutation.isPending}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFieldValue("verifyModules", [
                              ...verifyModules,
                              module,
                            ]);
                          } else {
                            setFieldValue(
                              "verifyModules",
                              verifyModules.filter((m) => m !== module),
                            );
                          }
                        }}
                      />
                      <Label
                        htmlFor={`verify-module-${module}`}
                        className="font-normal"
                      >
                        {module}
                      </Label>
                    </div>
                  ))}
                </div>

                {waitlistMutation.isError && (
                  <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>
                      We couldn't register your interest. Please try again.
                    </AlertDescription>
                  </Alert>
                )}

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={waitlistMutation.isPending}
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isValid || !dirty || waitlistMutation.isPending}
                  >
                    {waitlistMutation.isPending && <Spinner />}
                    Notify me
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};
